import type { CashbackBalance } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Button, Card, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import { adjustCashback, listCashbackBalances } from '../../lib/cashback.js';
import { formatDecimals } from '../../lib/money.js';

export function CashbackCustomersPage(): JSX.Element {
  const { t, i18n } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  // Cashback is a base-currency wallet — render with that currency's decimals.
  const fmt = (n: string | number): string => formatDecimals(n, baseCurrency, i18n.language);
  const canWrite = usePermission('cashback.write');
  const navigate = useNavigate();
  const [rows, setRows] = useState<CashbackBalance[] | null>(null);
  const [adjust, setAdjust] = useState<CashbackBalance | null>(null);

  const reload = async (): Promise<void> => {
    try { setRows(await listCashbackBalances()); } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!rows) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  const columns = [
    {
      id: 'customer', header: t('columns.customer', { defaultValue: 'Customer' }),
      cell: (r: CashbackBalance) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{r.partyName}</div>
          <div className="font-mono text-xs text-fg-muted">{r.partyCode}</div>
        </div>
      ),
      sortValue: (r: CashbackBalance) => r.partyName,
      searchValue: (r: CashbackBalance) => `${r.partyCode} ${r.partyName}`,
    },
    {
      id: 'balance', header: t('columns.balance', { defaultValue: 'Balance' }), align: 'end' as const,
      cell: (r: CashbackBalance) => <span className="font-mono font-semibold text-fg">{fmt(r.balance)}</span>,
      sortValue: (r: CashbackBalance) => Number(r.balance),
    },
    {
      id: 'earned', header: t('columns.earned', { defaultValue: 'Lifetime earned' }), align: 'end' as const,
      cell: (r: CashbackBalance) => <span className="font-mono text-fg-muted">{fmt(r.lifetimeEarned)}</span>,
      sortValue: (r: CashbackBalance) => Number(r.lifetimeEarned),
    },
    {
      id: 'redeemed', header: t('columns.redeemed', { defaultValue: 'Lifetime redeemed' }), align: 'end' as const,
      cell: (r: CashbackBalance) => <span className="font-mono text-fg-muted">{fmt(r.lifetimeRedeemed)}</span>,
      sortValue: (r: CashbackBalance) => Number(r.lifetimeRedeemed),
    },
    ...(canWrite ? [{
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: CashbackBalance) => (
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setAdjust(r); }}>
          {t('adjust.action', { defaultValue: 'Adjust' })}
        </Button>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('balances.title', { defaultValue: 'Customer balances' })}</h1>
      </header>
      {rows.length === 0 ? (
        <EmptyState title={t('balances.empty', { defaultValue: 'No customer has cashback yet.' })} />
      ) : (
        <Card className="p-0">
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => r.partyId}
            // A customer's row opens their profile (balance, invoices, statement).
            onRowClick={(r) => navigate(`/sales/parties/${r.partyId}`)}
            searchPlaceholder={t('balances.search', { defaultValue: 'Search customer…' })}
          />
        </Card>
      )}
      <AdjustDialog target={adjust} onClose={() => setAdjust(null)} onDone={reload} />
    </div>
  );
}

function AdjustDialog({
  target, onClose, onDone,
}: { target: CashbackBalance | null; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAmount(''); setNote(''); }, [target]);

  const submit = async (): Promise<void> => {
    if (!target || !amount.trim()) return;
    setBusy(true);
    try {
      await adjustCashback({ partyId: target.partyId, amount: amount.trim(), note: note.trim() || undefined });
      toast.success(t('adjust.success', { defaultValue: 'Balance adjusted.' }));
      onClose();
      await onDone();
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('adjust.title', { defaultValue: 'Adjust cashback' })}{target ? ` — ${target.partyName}` : ''}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <p className="text-xs text-fg-muted">{t('adjust.hint', { defaultValue: 'Positive grants cashback, negative deducts it.' })}</p>
          <FormField id="cb-adj-amt" label={t('adjust.amount', { defaultValue: 'Amount (+/−)' })}>
            <Input id="cb-adj-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 10 or -5" autoFocus />
          </FormField>
          <FormField id="cb-adj-note" label={t('adjust.note', { defaultValue: 'Note' })}>
            <Input id="cb-adj-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
          <Button onClick={() => void submit()} loading={busy} disabled={!amount.trim()}>{tc('actions.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
